import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { LogOut, Search, RefreshCw } from "lucide-react";
import { AdminLogin } from "../components/admin/AdminLogin";
import { ContactsTable, EstimationsTable } from "../components/admin/AdminTables";
import { API, STATUSES, locLabel } from "../components/admin/api";
import { useToast } from "../hooks/use-toast";
import { Toaster } from "../components/ui/toaster";

const TOKEN_KEY = "loyaris_admin_token";

const matches = (item, q) => {
  if (!q) return true;
  const hay = [item.name, item.email, item.phone, item.message, item.source, item.location && locLabel(item.location)]
    .filter(Boolean).join(" ").toLowerCase();
  return hay.includes(q.toLowerCase());
};

export default function Admin() {
  const { toast } = useToast();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [tab, setTab] = useState("contacts");
  const [data, setData] = useState({ contacts: [], estimations: [] });
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }, []);

  const load = useCallback(async () => {
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [c, e] = await Promise.all([
        axios.get(`${API}/admin/contacts`, { headers }),
        axios.get(`${API}/admin/estimations`, { headers }),
      ]);
      setData({ contacts: c.data, estimations: e.data });
    } catch (err) {
      if (err.response?.status === 401) logout();
    }
  }, [token, logout]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const login = (t) => {
    localStorage.setItem(TOKEN_KEY, t);
    setToken(t);
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/${tab}/${id}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      setData((d) => ({ ...d, [tab]: d[tab].map((x) => (x.id === id ? { ...x, status } : x)) }));
    } catch {
      toast({ title: "Mise à jour impossible", variant: "destructive" });
    }
  };

  const items = useMemo(
    () => data[tab].filter((x) => matches(x, query) && (statusFilter === "all" || (x.status || "new") === statusFilter)),
    [data, tab, query, statusFilter]
  );

  if (!token) return <AdminLogin onLogin={login} />;

  const count = (k, s) => data[k].filter((x) => (x.status || "new") === s).length;
  const tabBtn = (k, label) => (
    <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
      className={`rounded-full px-5 py-2 text-[14px] font-semibold transition-colors ${tab === k ? "bg-[#26241F] text-white" : "text-[#6e6a62] hover:bg-[#e8e1d4]"}`}>
      {label} <span className="ml-1 opacity-70">({data[k].length})</span>
    </button>
  );

  return (
    <div className="min-h-screen bg-[#F4F0E9]" data-testid="admin-dashboard">
      <header className="border-b border-[#e0d8c8] bg-[#FBF9F5]">
        <div className="mx-auto flex h-[70px] max-w-[1320px] items-center justify-between px-5 lg:px-8">
          <div className="font-serifDisplay text-[22px] text-[#26241F]"><span className="text-[#B08D57]">L</span>oyaris <span className="text-[14px] text-[#9a9488]">· Espace admin</span></div>
          <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-2 text-[14px] font-medium text-[#6e6a62] hover:text-[#26241F]">
            <LogOut className="h-4 w-4" /> Déconnexion
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-[1320px] px-5 py-10 lg:px-8">
        <div className="grid gap-4 sm:grid-cols-3">
          {STATUSES.map((s) => (
            <div key={s.value} className="rounded-2xl border border-[#e0d8c8] bg-[#FBF9F5] p-5" data-testid={`admin-stat-${s.value}`}>
              <div className="text-[12px] font-semibold uppercase tracking-[0.15em] text-[#9a9488]">{s.label}</div>
              <div className="mt-2 font-serifDisplay text-[32px] text-[#26241F]">{count("contacts", s.value) + count("estimations", s.value)}</div>
              <div className="text-[13px] text-[#6e6a62]">{count("contacts", s.value)} contacts · {count("estimations", s.value)} estimations</div>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
          <div className="flex gap-2">{tabBtn("contacts", "Demandes de contact")}{tabBtn("estimations", "Estimations de loyer")}</div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9a9488]" />
              <input data-testid="admin-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher…"
                className="w-[240px] rounded-lg border border-[#ded5c4] bg-white py-2.5 pl-9 pr-3 text-[14px] outline-none focus:border-[#B08D57]" />
            </div>
            <select data-testid="admin-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-lg border border-[#ded5c4] bg-white px-3 py-2.5 text-[14px] outline-none focus:border-[#B08D57]">
              <option value="all">Tous les statuts</option>
              {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <button data-testid="admin-refresh-button" onClick={load} aria-label="Actualiser" className="rounded-lg border border-[#ded5c4] bg-white p-2.5 text-[#6e6a62] hover:text-[#26241F]">
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="mt-5 overflow-x-auto rounded-2xl border border-[#e0d8c8] bg-[#FBF9F5]">
          {tab === "contacts" ? <ContactsTable items={items} onStatus={updateStatus} /> : <EstimationsTable items={items} onStatus={updateStatus} />}
        </div>
      </main>
      <Toaster />
    </div>
  );
}
